import { Component, ElementRef, EventEmitter, forwardRef, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { PhoneInfo } from '@core/model/phone-info';

import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { getSweetAlertOptions } from '@commons/utility/util';

import { show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

import { PhoneValidationResult } from '@core/model/phone-validation-result';
import { retryWithDelay } from '@core/operators/retry-delay';

import { ValidatorService } from '../validator.service';
import { PhoneVerificationService } from './phone-verification.service';

import { INPUT_PATTERNS_CONSTANT } from './rapi-phone.constant';
@Component({
  selector: 'app-rapi-phone',
  templateUrl: './rapi-phone.component.html',
  styleUrls: ['./rapi-phone.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RapiPhoneComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiPhoneComponent),
      multi: true,
    },
  ],
})
export class RapiPhoneComponent implements OnInit, ControlValueAccessor, OnDestroy {
  form: FormGroup;
  subscriptions: Subscription[] = [];
  existingValidationMsg: string;
  @ViewChild('phoneInput', { static: false }) phoneInput: ElementRef;
  invalidNumberMsg: string;

  @Output() public focusEventPhone: EventEmitter<void>;

  constructor(
    private formBuilder: FormBuilder,
    private phoneVerificationService: PhoneVerificationService,
    private validatorService: ValidatorService,
    private router: Router
  ) {
    this.focusEventPhone = new EventEmitter<void>();
    this.form = this.formBuilder.group(
      {
        phone: ['', Validators.required, Validators.pattern(INPUT_PATTERNS_CONSTANT.NUMBER)],
      },
      {
        updateOn: 'blur',
      }
    );
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.form.controls['phone'].setAsyncValidators([this.asyncValidatePhonePipe()]);
    this.form.controls['phone'].statusChanges.subscribe((status) => {
      this.form.updateValueAndValidity();
    });
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): PhoneInfo {
    return this.form.value;
  }

  set value(value: PhoneInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get phoneControl() {
    return this.form.controls.phone;
  }

  public onFocusEmitInput(): void {
    this.focusEventPhone.emit();
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
      this.phoneInput.nativeElement.focus();
    }
  }

  validate(_: FormControl) {
    if (!this.form.controls['phone'].value || this.form.controls['phone'].invalid) {
      return this.form.valid ? null : { phone: { valid: false } };
    }
  }

  private _asyncValidatePhoneNumber(mobileNumber: string): Observable<ValidationErrors | null> | null {
    return this.validatorService.validatePhoneNumber(mobileNumber).pipe(
      map((response: PhoneValidationResult) => {
        this.invalidNumberMsg = response.message;
        return response.result;
      })
    );
  }

  showAlertMessage(message: string, confirmButtonText: string) {
    let options = getSweetAlertOptions(message, confirmButtonText);

    Swal.fire(options).then((result) => {
      this.phoneControl.setValue('');
    });
  }

  private isValidPattern(phoneNumber: string | null): boolean {
    return phoneNumber ? INPUT_PATTERNS_CONSTANT.NUMBER_RANGE.test(phoneNumber) : false;
  }

  asyncValidatePhonePipe(): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
      if (!this.isValidPattern(control.value)) {
        return of({ pattern: true });
      }

      if (control != null) {
        return of(control.value).pipe(
          debounceTime(3000),
          switchMap((value) => {
            //phoneControl has the same value than value
            if (this.phoneControl.value) {
              return this._asyncValidatePhoneNumber(this.phoneControl.value);
            }
          }),
          switchMap((value) => {
            if (value == null) {
              if (this.phoneControl.value) {
                return this.phoneVerificationService.existsByPhone(this.phoneControl.value).pipe(
                  map((result) => {
                    if (result.response == true) {
                      this.showAlertMessage(result.message, 'Rectificar celular');
                      return {
                        existing: true,
                      };
                    } else {
                      return value;
                    }
                  })
                );
              } else {
                return of({ phoneNumber: true });
              }
            } else {
              return of(value);
            }
          }),
          retryWithDelay(1000, 3),
          catchError((error: Error) => {
            show_popup(
              TITLE_ERROR,
              'Lo sentimos, ocurrió un error al validar tu número de celular. Inténtalo de nuevo  P-131'
            );
            this.phoneControl.setValue(null);
            return EMPTY;
          })
        );
      }
      return EMPTY;
    };
  }

  public checkIsNumber(event: KeyboardEvent): void {
    const char = event.key;

    if (!INPUT_PATTERNS_CONSTANT.NUMBER.test(char)) {
      event.preventDefault();
    }
  }
}
